import { Suspense, useEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { AdaptiveDpr, Sparkles } from '@react-three/drei'
import { Bloom, EffectComposer, Noise, Vignette } from '@react-three/postprocessing'
import { BlendFunction } from 'postprocessing'
import * as THREE from 'three'
import { echoColors, experienceState, type EchoChoice } from '../experienceState'

const vertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uPulse;
  varying vec3 vWorldNormal;
  varying vec3 vPosition;
  varying float vNoise;

  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+10.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy,h.x);
    vec3 p1 = vec3(a0.zw,h.y);
    vec3 p2 = vec3(a1.xy,h.z);
    vec3 p3 = vec3(a1.zw,h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }

  void main() {
    float breath = sin(uTime * 0.82) * 0.5 + 0.5;
    float n = snoise(position * 1.75 + vec3(uTime * 0.11, -uTime * 0.08, uTime * 0.06));
    float fissure = sin(position.y * 8.0 + n * 3.0 + uTime * 0.45) * 0.5 + 0.5;
    float displacement = n * (0.13 + uPulse * 0.24) + fissure * 0.025 + breath * 0.018;
    vec3 transformed = position + normal * displacement;
    vec4 worldPosition = modelMatrix * vec4(transformed, 1.0);
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vPosition = worldPosition.xyz;
    vNoise = n;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`

const fragmentShader = /* glsl */ `
  uniform float uTime;
  uniform vec3 uAccent;
  uniform float uPulse;
  varying vec3 vWorldNormal;
  varying vec3 vPosition;
  varying float vNoise;

  void main() {
    vec3 viewDirection = normalize(cameraPosition - vPosition);
    float fresnel = pow(1.0 - max(dot(viewDirection, vWorldNormal), 0.0), 2.5);
    float contour = smoothstep(0.45, 0.52, sin((vPosition.y + vNoise * 0.16) * 29.0 + uTime * 0.3) * 0.5 + 0.5);
    float hot = smoothstep(0.36, 0.75, vNoise * 0.5 + 0.5);
    vec3 coal = vec3(0.018, 0.014, 0.012);
    vec3 bone = vec3(0.93, 0.88, 0.78);
    vec3 color = mix(coal, uAccent * 0.58, hot);
    color = mix(color, bone, contour * 0.2);
    color += uAccent * fresnel * (1.1 + uPulse * 1.8);
    gl_FragColor = vec4(color, 1.0);
  }
`

const veilVertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uProgress;
  varying vec2 vUv;
  varying float vWave;
  void main() {
    vUv = uv;
    vec3 p = position;
    float wave = sin(p.x * 0.55 + uTime * 0.17) * 0.42 + cos(p.y * 0.7 - uTime * 0.13) * 0.28;
    p.z += wave + sin((p.x + p.y) * 0.9 + uProgress * 10.0) * 0.13;
    vWave = wave;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`

const veilFragmentShader = /* glsl */ `
  uniform vec3 uAccent;
  uniform float uProgress;
  varying vec2 vUv;
  varying float vWave;
  void main() {
    float edge = smoothstep(0.0, 0.34, vUv.y) * smoothstep(1.0, 0.66, vUv.y);
    float glow = pow(max(0.0, 1.0 - abs(vUv.x - 0.5) * 1.35), 3.0);
    vec3 color = uAccent * (0.08 + glow * 0.13 + vWave * 0.025);
    float alpha = edge * (0.16 + glow * 0.14) * (0.8 + uProgress * 0.2);
    gl_FragColor = vec4(color, alpha);
  }
`

function damp(current: number, target: number, lambda: number, delta: number) {
  return THREE.MathUtils.lerp(current, target, 1 - Math.exp(-lambda * delta))
}

function frameValue(progress: number, values: number[]) {
  const scaled = progress * (values.length - 1)
  const index = Math.min(values.length - 2, Math.floor(scaled))
  return THREE.MathUtils.lerp(values[index], values[index + 1], THREE.MathUtils.smoothstep(scaled - index, 0, 1))
}

function SceneRig() {
  const { camera } = useThree()
  const lookAt = useRef(new THREE.Vector3())

  useFrame(({ pointer }, delta) => {
    experienceState.smoothProgress = damp(
      experienceState.smoothProgress,
      experienceState.progress,
      experienceState.reducedMotion ? 18 : 3.5,
      delta,
    )
    const p = experienceState.smoothProgress
    const targetX = frameValue(p, [0, -0.45, 0.5, -0.3, 0.25, 0]) + pointer.x * 0.18
    const targetY = frameValue(p, [0.05, 0.35, -0.15, 0.18, -0.1, 0]) + pointer.y * 0.12
    const targetZ = frameValue(p, [7.4, 6.7, 7.1, 6.55, 7.5, 8.2])
    camera.position.x = damp(camera.position.x, targetX, 2.3, delta)
    camera.position.y = damp(camera.position.y, targetY, 2.3, delta)
    camera.position.z = damp(camera.position.z, targetZ, 2.3, delta)
    lookAt.current.set(pointer.x * 0.08, pointer.y * 0.05, frameValue(p, [0, -0.15, 0.1, 0, -0.2, 0]))
    camera.lookAt(lookAt.current)
  })
  return null
}

function MemoryCore({ choice }: { choice: EchoChoice }) {
  const group = useRef<THREE.Group>(null)
  const material = useRef<THREE.ShaderMaterial>(null)
  const shell = useRef<THREE.Mesh>(null)
  const inner = useRef<THREE.Mesh>(null)
  const accent = useRef(new THREE.Color(echoColors[choice]))
  const targetAccent = useMemo(() => new THREE.Color(echoColors[choice]), [choice])
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uPulse: { value: 0 },
    uAccent: { value: new THREE.Color(echoColors.wild) },
  }), [])

  useFrame(({ clock, pointer, viewport }, delta) => {
    if (!group.current || !material.current) return
    const p = experienceState.smoothProgress
    const compact = viewport.width < 5.5
    const spread = compact ? 0.72 : 1
    const targetX = frameValue(p, [0, -1.74, 1.7, -1.72, 0, 0]) * spread
    const targetY = frameValue(p, [0.05, 0.1, -0.18, 0.12, 0.1, -0.15])
    group.current.position.x = damp(group.current.position.x, targetX, 3.4, delta)
    group.current.position.y = damp(group.current.position.y, targetY, 3.4, delta)
    group.current.rotation.y += delta * (0.1 + Math.abs(experienceState.velocity) * 0.001)
    group.current.rotation.x = damp(group.current.rotation.x, pointer.y * 0.12, 2, delta)
    group.current.rotation.z = damp(group.current.rotation.z, pointer.x * -0.08, 2, delta)

    experienceState.releasePulse = Math.max(0, experienceState.releasePulse - delta * 0.24)
    accent.current.lerp(targetAccent, 1 - Math.exp(-delta * 2.1))
    material.current.uniforms.uTime.value = clock.elapsedTime
    material.current.uniforms.uPulse.value = experienceState.releasePulse
    material.current.uniforms.uAccent.value.copy(accent.current)
    if (shell.current) shell.current.rotation.y -= delta * 0.065
    if (inner.current) {
      const s = 1 + Math.sin(clock.elapsedTime * 1.5) * 0.07 + experienceState.releasePulse * 0.85
      inner.current.scale.setScalar(s)
    }
  })

  return (
    <group ref={group}>
      <mesh castShadow>
        <icosahedronGeometry args={[1.06, 5]} />
        <shaderMaterial ref={material} uniforms={uniforms} vertexShader={vertexShader} fragmentShader={fragmentShader} />
      </mesh>
      <mesh ref={shell} scale={1.14}>
        <icosahedronGeometry args={[1, 2]} />
        <meshBasicMaterial color={echoColors[choice]} wireframe transparent opacity={0.12} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh ref={inner} scale={0.23}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial color="#fff4df" toneMapped={false} />
      </mesh>
      <OrbitRing radius={1.56} tilt={[1.2, 0.15, -0.42]} speed={0.11} color={echoColors[choice]} />
      <OrbitRing radius={1.88} tilt={[0.35, 1.05, 0.3]} speed={-0.075} color="#d9cbb2" dashed />
      <OrbitRing radius={2.22} tilt={[1.48, 0.25, 0.6]} speed={0.038} color={echoColors[choice]} />
      <CoreDust choice={choice} />
    </group>
  )
}

function OrbitRing({ radius, tilt, speed, color, dashed = false }: {
  radius: number
  tilt: [number, number, number]
  speed: number
  color: string
  dashed?: boolean
}) {
  const ring = useRef<THREE.Mesh>(null)
  useFrame((_, delta) => {
    if (ring.current) ring.current.rotation.z += delta * speed
  })
  return (
    <mesh ref={ring} rotation={tilt}>
      <torusGeometry args={[radius, dashed ? 0.006 : 0.009, 6, dashed ? 96 : 192, dashed ? Math.PI * 1.64 : Math.PI * 2]} />
      <meshBasicMaterial color={color} transparent opacity={dashed ? 0.32 : 0.46} blending={THREE.AdditiveBlending} toneMapped={false} />
    </mesh>
  )
}

function CoreDust({ choice }: { choice: EchoChoice }) {
  const points = useRef<THREE.Points>(null)
  const positions = useMemo(() => {
    const data = new Float32Array(280 * 3)
    for (let i = 0; i < 280; i++) {
      const r = 1.35 + Math.random() * 1.45
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      data[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      data[i * 3 + 1] = r * Math.cos(phi)
      data[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta)
    }
    return data
  }, [])
  useFrame((_, delta) => {
    if (points.current) {
      points.current.rotation.y += delta * 0.032
      points.current.rotation.x -= delta * 0.012
      points.current.scale.setScalar(1 + experienceState.releasePulse * 1.25)
    }
  })
  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color={echoColors[choice]} size={0.022} transparent opacity={0.68} sizeAttenuation blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
    </points>
  )
}

function createMorphTargets(count: number) {
  const targets = Array.from({ length: 5 }, () => new Float32Array(count * 3))
  const golden = Math.PI * (3 - Math.sqrt(5))

  for (let i = 0; i < count; i++) {
    const t = i / (count - 1)
    const theta = golden * i

    // A broken celestial shell.
    const y = 1 - t * 2
    const sphereR = Math.sqrt(Math.max(0, 1 - y * y))
    const radius = 2.35 + Math.sin(i * 12.9898) * 0.42
    targets[0][i * 3] = Math.cos(theta) * sphereR * radius
    targets[0][i * 3 + 1] = y * radius
    targets[0][i * 3 + 2] = Math.sin(theta) * sphereR * radius

    // A fork in time: two helices.
    const branch = i % 2 === 0 ? -1 : 1
    const helixAngle = t * Math.PI * 8 + branch * Math.PI
    targets[1][i * 3] = Math.cos(helixAngle) * (1.28 + branch * 0.14)
    targets[1][i * 3 + 1] = (t - 0.5) * 5.9
    targets[1][i * 3 + 2] = Math.sin(helixAngle) * 1.18 + branch * 0.22

    // A phyllotaxis bloom.
    const flowerR = Math.sqrt(t) * 3.45
    targets[2][i * 3] = Math.cos(theta) * flowerR
    targets[2][i * 3 + 1] = Math.sin(flowerR * 2.25) * 0.42 - 0.35 + (1 - t) * 0.65
    targets[2][i * 3 + 2] = Math.sin(theta) * flowerR

    // A tidal ribbon suspended overhead.
    const waveX = (t - 0.5) * 7.3
    targets[3][i * 3] = waveX
    targets[3][i * 3 + 1] = Math.sin(t * Math.PI * 5.0) * 1.25 + Math.cos(i * 0.71) * 0.28
    targets[3][i * 3 + 2] = Math.cos(t * Math.PI * 3.0) * 1.55 + Math.sin(i * 0.47) * 0.24

    // A doorway: concentric vertical ellipses.
    const loop = i % 7
    const loopAngle = (i / count * Math.PI * 14) % (Math.PI * 2)
    const loopR = 1.15 + loop * 0.27
    targets[4][i * 3] = Math.cos(loopAngle) * loopR
    targets[4][i * 3 + 1] = Math.sin(loopAngle) * loopR * 1.42
    targets[4][i * 3 + 2] = (loop - 3) * 0.2
  }
  return targets
}

function MorphField({ choice }: { choice: EchoChoice }) {
  const mesh = useRef<THREE.InstancedMesh>(null)
  const count = 460
  const targets = useMemo(() => createMorphTargets(count), [])
  const dummy = useMemo(() => new THREE.Object3D(), [])
  const color = useMemo(() => new THREE.Color(), [])

  useEffect(() => {
    if (!mesh.current) return
    const base = new THREE.Color(echoColors[choice])
    const bone = new THREE.Color('#ede5d5')
    for (let i = 0; i < count; i++) {
      color.copy(base).lerp(bone, (i % 11) / 28)
      mesh.current.setColorAt(i, color)
    }
    if (mesh.current.instanceColor) mesh.current.instanceColor.needsUpdate = true
  }, [choice, color])

  useFrame(({ clock }, delta) => {
    if (!mesh.current) return
    const p = THREE.MathUtils.clamp(experienceState.smoothProgress * 4.35, 0, 4)
    const from = Math.min(3, Math.floor(p))
    const to = Math.min(4, from + 1)
    const mix = THREE.MathUtils.smootherstep(p - from, 0, 1)
    const pulse = experienceState.releasePulse

    for (let i = 0; i < count; i++) {
      const offset = i * 3
      const pulseScale = 1 + pulse * (0.45 + (i % 17) / 18)
      dummy.position.set(
        THREE.MathUtils.lerp(targets[from][offset], targets[to][offset], mix) * pulseScale,
        THREE.MathUtils.lerp(targets[from][offset + 1], targets[to][offset + 1], mix) * pulseScale,
        THREE.MathUtils.lerp(targets[from][offset + 2], targets[to][offset + 2], mix) * pulseScale,
      )
      const shardScale = 0.44 + (i % 13) / 14
      const flicker = 1 + Math.sin(clock.elapsedTime * 1.1 + i) * 0.13
      dummy.scale.set(0.034 * shardScale, 0.095 * shardScale * flicker, 0.034 * shardScale)
      dummy.rotation.set(i * 0.43 + clock.elapsedTime * 0.05, i * 0.71, i * 0.19)
      dummy.updateMatrix()
      mesh.current.setMatrixAt(i, dummy.matrix)
    }
    mesh.current.instanceMatrix.needsUpdate = true
    mesh.current.rotation.y += delta * 0.018
  })

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]} frustumCulled={false}>
      <octahedronGeometry args={[1, 0]} />
      <meshStandardMaterial vertexColors roughness={0.38} metalness={0.82} emissive="#1e0904" emissiveIntensity={0.35} />
    </instancedMesh>
  )
}

function MemoryVeil({ choice }: { choice: EchoChoice }) {
  const material = useRef<THREE.ShaderMaterial>(null)
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uProgress: { value: 0 },
    uAccent: { value: new THREE.Color(echoColors.wild) },
  }), [])
  const target = useMemo(() => new THREE.Color(echoColors[choice]), [choice])

  useFrame(({ clock }, delta) => {
    if (!material.current) return
    material.current.uniforms.uTime.value = clock.elapsedTime
    material.current.uniforms.uProgress.value = experienceState.smoothProgress
    material.current.uniforms.uAccent.value.lerp(target, 1 - Math.exp(-delta * 1.5))
  })

  return (
    <mesh position={[0, -1.8, -4.6]} rotation={[-0.72, 0, 0]}>
      <planeGeometry args={[18, 11, 80, 50]} />
      <shaderMaterial
        ref={material}
        uniforms={uniforms}
        vertexShader={veilVertexShader}
        fragmentShader={veilFragmentShader}
        transparent
        depthWrite={false}
        side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  )
}

function DistantField({ choice }: { choice: EchoChoice }) {
  const group = useRef<THREE.Group>(null)
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.z -= delta * 0.0025
  })
  return (
    <group ref={group}>
      <Sparkles count={180} scale={[15, 9, 7]} size={1.2} speed={0.08} opacity={0.45} color="#f3ead8" noise={[1, 1, 1]} />
      <Sparkles count={65} scale={[10, 7, 5]} size={2.1} speed={0.15} opacity={0.52} color={echoColors[choice]} noise={[1, 0.8, 1]} />
    </group>
  )
}

function World({ choice }: { choice: EchoChoice }) {
  const world = useRef<THREE.Group>(null)
  useFrame(({ pointer }, delta) => {
    if (!world.current) return
    world.current.rotation.y = damp(world.current.rotation.y, pointer.x * 0.035, 1.4, delta)
    world.current.rotation.x = damp(world.current.rotation.x, pointer.y * -0.022, 1.4, delta)
  })

  return (
    <>
      <ambientLight intensity={0.12} />
      <directionalLight position={[4, 6, 5]} intensity={2.4} color="#f2d7b4" />
      <pointLight position={[-4, -2, 3]} intensity={28} distance={10} decay={2} color={echoColors[choice]} />
      <pointLight position={[3, 2, -1]} intensity={18} distance={8} decay={2} color="#7ccfc3" />
      <group ref={world}>
        <MemoryCore choice={choice} />
        <MorphField choice={choice} />
      </group>
      <MemoryVeil choice={choice} />
      <DistantField choice={choice} />
    </>
  )
}

export function ArchiveScene({ choice }: { choice: EchoChoice }) {
  return (
    <div className="canvas-wrap" aria-hidden="true">
      <Canvas
        dpr={[1, 1.75]}
        camera={{ position: [0, 0, 7.4], fov: 38, near: 0.1, far: 100 }}
        gl={{ antialias: false, alpha: true, powerPreference: 'high-performance' }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping
          gl.toneMappingExposure = 1.08
          gl.outputColorSpace = THREE.SRGBColorSpace
        }}
      >
        <color attach="background" args={['#070706']} />
        <fog attach="fog" args={['#070706', 8, 22]} />
        <Suspense fallback={null}>
          <SceneRig />
          <World choice={choice} />
          <EffectComposer multisampling={0} enableNormalPass={false}>
            <Bloom mipmapBlur intensity={1.15} luminanceThreshold={0.52} luminanceSmoothing={0.2} />
            <Noise opacity={0.085} blendFunction={BlendFunction.SOFT_LIGHT} />
            <Vignette eskil={false} offset={0.18} darkness={0.78} />
          </EffectComposer>
          <AdaptiveDpr pixelated />
        </Suspense>
      </Canvas>
    </div>
  )
}
